import type { DrawingModuleType } from "../DrawingSingleton";

/**
 * Font management utilities for loading and registering fonts.
 *
 * Based on ODA example pattern:
 * 1. Load font to root '/' using FS_createDataFile
 * 2. Register font with services.ttfFileNameByDescriptor
 * 3. Use typeface name when setting on text style with setFont()
 */

/** Track loaded fonts to avoid duplicates */
const loadedFonts = new Set<string>();

/**
 * Load a font file into the Emscripten virtual filesystem.
 * Uses Module.FS_createDataFile as per ODA examples.
 * @param module The ODA module
 * @param fontName The filename (e.g., "arial.ttf")
 * @param fontData The font file data as Uint8Array
 * @returns The font filename
 */
export function loadFontToFS(
  module: DrawingModuleType,
  fontName: string,
  fontData: Uint8Array,
): string {
  // Check if already loaded
  if (loadedFonts.has(fontName)) {
    return fontName;
  }

  try {
    // Load to root '/' directory using Module.FS_createDataFile
    // This is the correct Emscripten API as shown in ODA examples
    (module as any).FS_createDataFile(
      "/",
      fontName,
      fontData,
      true,
      true,
      true,
    );

    loadedFonts.add(fontName);
  } catch (e) {
    console.warn(`[Font] Error loading font ${fontName}:`, e);
  }

  return fontName;
}

/**
 * Load a font from a URL into the virtual filesystem.
 * @param module The ODA module
 * @param url The URL to fetch the font from
 * @param fontName Optional custom name (defaults to URL filename)
 * @returns Promise resolving to the font filename
 */
export async function loadFontFromURL(
  module: DrawingModuleType,
  url: string,
  fontName?: string,
): Promise<string> {
  const name = fontName ?? url.split("/").pop() ?? "font.ttf";

  // Check if already loaded
  if (loadedFonts.has(name)) {
    return name;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch font: ${response.statusText}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const fontData = new Uint8Array(arrayBuffer);

  return loadFontToFS(module, name, fontData);
}

/**
 * Load a font from a File object (e.g., from file input).
 * @param module The ODA module
 * @param file The File object
 * @returns Promise resolving to the font filename
 */
export async function loadFontFromFile(
  module: DrawingModuleType,
  file: File,
): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const fontData = new Uint8Array(arrayBuffer);

  return loadFontToFS(module, file.name, fontData);
}

/**
 * Get list of currently loaded font names.
 */
export function getLoadedFonts(): string[] {
  return Array.from(loadedFonts);
}

/**
 * Check if a font is loaded.
 */
export function isFontLoaded(fontName: string): boolean {
  return loadedFonts.has(fontName);
}
